import { Button } from '~/components/general/Button'

import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import { useEffect } from 'react'
import { trackEvent } from '~/utils/tracking'
import { FlexContainer } from '~/components/Grid'
import { createOptimizedContentfulImage } from '~/libs/contentful/helpers'
import { TypeHomePageBlock } from '~/types/contentful'
import { Asset } from 'contentful'

gsap.registerPlugin(ScrollTrigger)

type NavigateYourCareerProps = {
  section: TypeHomePageBlock<'WITHOUT_UNRESOLVABLE_LINKS', string>
}

export const NavigateYourCareer = ({ section }: NavigateYourCareerProps) => {
  const data = section.fields

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          start: 'top center',
          trigger: '.navigate-animate-trigger',
        },
      })
      tl.to('.animate-after-card1', 0.5, {
        transform: 'translate(-48.5%, -51.5%)',
      })
        .to(
          '.animate-after-card2',
          0.5,
          {
            transform: 'translate(-47%, -53%)',
          },
          '-=0.5',
        )
        .to(
          '.animate-icon-card1',
          0.5,
          {
            x: -15,
          },
          '-=0.5',
        )
        .to(
          '.animate-icon-card2',
          0.5,
          {
            x: 15,
            y: -7,
            scale: 1.2,
          },
          '-=0.5',
        )
        .to(
          '.animate-icon-card3',
          0.5,
          {
            x: -20,
            rotate: -25,
          },
          '-=0.5',
        )
        .fromTo(
          '.animate-footer-date',
          1.4,
          {
            opacity: 0,
          },
          {
            opacity: 1,
          },
          '-=0.5',
        )
    })

    return () => ctx.revert()
  }, [])

  const handleCTABtnClick = () => {
    trackEvent('homepage_click_cta', {
      block: data.blockId,
      target: data.buttonLink as string,
      homepage_CTA: data.buttonName as string,
    })
  }

  return (
    <>
      <section
        data-block-id={data.blockId}
        className="my-25 -mx-8 max-xl:p-11 max-lg:my-0 max-lg:-mx-6 max-md:-mx-5 max-md:pt-4 max-md:pb-5 max-md:px-4 navigate-animate-trigger"
      >
        <FlexContainer
          style={{ borderRadius: '40px' }}
          flexWrap={'nowrap'}
          justify={'start'}
          className="pt-22 max-xl:pt-0 max-lg:gap-8 max-md:gap-7 max-md:items-center max-md:p-[0_0_31px_0] max-lg:flex-col max-lg:bg-neutral-25 gap-16 bg-neutral-5 overflow-hidden"
        >
          <div
            className="max-md:max-h-[277px] max-md:min-h-[350px] max-lg:min-h-[450px] max-md:[&_img]:max-h-[277px] max-md:[&_img]:object-contain max-xl:rounded-none max-lg:max-w-none max-lg:w-full self-end flex justify-center items-center object-cover relative rounded-[0px_8px_0px_0px] max-w-[calc(50%_-_15px)] h-[490px]"
            style={{
              backgroundImage: data.images
                ? `url(${createOptimizedContentfulImage({ file: (data.images[0] as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>).fields.file! })})`
                : '',
              flexBasis: '50%',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
            }}
          >
            <div className="absolute left-0 top-0 right-0 bottom-0">
              <div
                className="[&>:nth-child(3)]:opacity-80 [&>:nth-child(2)]:opacity-100 [&>:nth-child(2)]:z-1 rounded-lg bg-neutral-10 absolute flex flex-col p-6 max-md:w-50 max-md:min-h-[275px] min-h-[345px] w-[327px] -translate-x-1/2 -translate-y-1/2 first:z-2 "
                style={{
                  boxShadow:
                    '0px 2.320819139480591px 4.641638278961182px 0px rgba(15, 56, 113, 0.08), 0px 1.1604095697402954px 2.320819139480591px 0px rgba(15, 56, 113, 0.12), 0px 0px 1.1604095697402954px 0px rgba(15, 56, 113, 0.32)',
                  left: '49.3%',
                  top: '49.2%',
                }}
              >
                <div className="text-center relative">
                  <img
                    className="w-36 h-36 my-0 mx-auto overflow-hidden object-cover mb-4 max-md:w-21 max-md:h-21"
                    src="./images/navigate_photo.jpg"
                    alt=""
                    loading="lazy"
                    style={{ borderRadius: '50%' }}
                  />
                  <img
                    src={'./images/animate/navigate/icon_1.jpg'}
                    alt=""
                    className={'animate-icon-card1'}
                    loading="lazy"
                    style={{
                      position: 'absolute',
                      left: '10.4%',
                      top: '9.8%',
                      height: 36,
                      borderRadius: 17,
                      zIndex: -1,
                    }}
                  />
                  <img
                    src={'./images/animate/navigate/icon_2.svg'}
                    alt=""
                    className={'animate-icon-card2'}
                    loading="lazy"
                    style={{
                      position: 'absolute',
                      left: '63%',
                      bottom: '29%',
                    }}
                  />

                  <img
                    src={'./images/animate/navigate/icon_3.png'}
                    alt=""
                    className={'animate-icon-card3'}
                    loading="lazy"
                    style={{
                      position: 'absolute',
                      left: '25%',
                      top: '62%',
                    }}
                  />
                </div>
                <div className="text-center text-s max-md:font-[14px] max-md:leading-4">
                  Personal Branding <br />{' '}
                  <span className="text-neutral-50">with Bryn Williams</span>
                </div>
                <div
                  className="text-center bg-white text-green-40 text-xs py-3 px-15 mt-auto max-md:px-4 animate-footer-date"
                  style={{ borderRadius: '40px' }}
                >
                  Scheduled at 16:00
                </div>
              </div>

              <div
                className="rounded-lg bg-neutral-10 absolute flex flex-col p-6 max-md:w-50 max-md:min-h-[275px] min-h-[345px] w-[327px] -translate-x-1/2 -translate-y-1/2 first:z-2 animate-after-card1"
                style={{
                  boxShadow:
                    '0px 2.320819139480591px 4.641638278961182px 0px rgba(15, 56, 113, 0.08), 0px 1.1604095697402954px 2.320819139480591px 0px rgba(15, 56, 113, 0.12), 0px 0px 1.1604095697402954px 0px rgba(15, 56, 113, 0.32)',
                  left: '49.3%',
                  top: '49.2%',
                }}
              ></div>
              <div
                className="rounded-lg bg-neutral-10 absolute flex flex-col p-6 max-md:w-50 max-md:min-h-[275px] min-h-[345px] w-[327px] -translate-x-1/2 -translate-y-1/2 first:z-2 animate-after-card2"
                style={{
                  boxShadow:
                    '0px 2.320819139480591px 4.641638278961182px 0px rgba(15, 56, 113, 0.08), 0px 1.1604095697402954px 2.320819139480591px 0px rgba(15, 56, 113, 0.12), 0px 0px 1.1604095697402954px 0px rgba(15, 56, 113, 0.32)',
                  left: '49.3%',
                  top: '49.2%',
                }}
              ></div>
            </div>
          </div>
          <FlexContainer
            flexWrap={'nowrap'}
            id="navigate-hero"
            className="max-lg:gap-0 max-lg:max-w-[70%] max-lg:my-0 max-lg:mx-auto max-lg:p-[20px_55px_50px] relative flex-col gap-8 max-w-[375px] items-start ml-0 max-xl:justify-center max-md:p-0 max-md:justify-end max-md:m-0 max-md:items-stretch"
          >
            <h2 className="text-l max-lg:mb-5 max-md:mb-4 max-md:text-center">{data.title}</h2>
            <p className="max-lg:mb-7 max-md:hidden text-s">{data.description}</p>
            <Button
              text={data.buttonName}
              type={'link'}
              className="max-md:!mx-auto"
              href={data.buttonLink}
              onClick={handleCTABtnClick}
            />
          </FlexContainer>
        </FlexContainer>
      </section>
    </>
  )
}
